// site-details.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import SignatureCanvas from 'react-signature-canvas';
import clsx from 'clsx';
import useAuthStore from '@store/useAuthStore';
import useSiteStore from '@store/useSiteStore';
import useEstimateStore from '@store/useEstimateStore';
import { Card, Col, Descriptions, Row, Skeleton, Tag, Typography, Modal, Divider } from 'antd';
import { Button, Form, Select } from 'antd';
import { DownloadOutlined, FormOutlined } from '@ant-design/icons';
import BasicLayout from '@/components/common/layout/basic';
import API from '@/api/api';
import i18n from '@/i18n';
import { t } from '@/utils/translator';
import {
  getPreferredTimezone,
  formatDateWithTimezone,
  formatDateRange,
} from '@/utils/dateFormatter';

import ProjectAnnouncementModal from '@/components/user/site-details/modals/ProjectAnnouncementModal';
import SiteEditModal from '@/components/user/site-details/modals/SiteEditModal';
import RemainingDays from '@/components/utils/RemainingDays';
import SiteCard from '@/components/sites/SiteCard';
import EstimateCycleTable from '@/components/user/site-details/EstimateCycleTable2';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import MaterialSearchTable from '@components/sites/MaterialSearchTable';
import { STYLE_CONSTANTS } from '@/constants/siteData';
import { StatusColor, CycleStatus } from '@constants/status';
import { ProjectStatusCode, ProjectStatusCodeColor } from '@constants/status';

dayjs.extend(duration);

function UserSiteDetailedv2() {
  const userTimezone = getPreferredTimezone();
  const user = useAuthStore((state) => state.user);
  const {
    EstimateInfo,
    fetchEstimateInfo: fetchEstimateInfoStore,
    fetchEstimateCycles,
    ActiveEstimateSiteInfoId,
    setActiveEstimateSiteInfoId,
  } = useEstimateStore();
  const { EstimateSites, fetchEstimateSites } = useSiteStore();

  const { project_id, estimate_id, company_id } = useParams();
  const [PageLoading, setPageLoading] = useState({});

  const [templateType, setTemplateType] = useState('latest'); // 'legacy' or 'latest'
  const [activeTabKey, setActiveTabKey] = useState(null);

  // 모달 관련
  const [isShowProjectAnnouncementModal, showProjectAnnouncementModal] = useState(false);
  const [isShowProjectPolicyModal, showProjectPolicyModal] = useState(false);
  const [isShowSiteEditModal, setSiteEditModal] = useState(false);
  const [isSiteEditModal, setSiteModalEdittype] = useState(false);
  const [isShowScope3TemplateModal, setShowScope3TemplateModal] = useState(false);

  const fetchEstimateInfo = useCallback(async () => {
    try {
      setPageLoading(true);

      await fetchEstimateInfoStore(estimate_id);
      await fetchEstimateSites(estimate_id);
    } catch (error) {
      console.log(` ~ error => `, error);
    } finally {
      setPageLoading(false);
      showProjectAnnouncementModal(true);
    }
  }, [estimate_id, setPageLoading, fetchEstimateInfoStore, fetchEstimateSites]);

  useEffect(() => {
    fetchEstimateInfo();
  }, [project_id, estimate_id, company_id, fetchEstimateInfo]);

  useEffect(() => {
    if (typeof EstimateInfo.policy_agree !== 'undefined' && !EstimateInfo.policy_agree) {
      showProjectPolicyModal(true);
    }
  }, [EstimateInfo, showProjectPolicyModal]);

  // EstimateSites가 로드되면 첫 번째 탭을 기본으로 설정
  useEffect(() => {
    if (EstimateSites.length > 0 && !activeTabKey) {
      const estimate_site_info_id = EstimateSites[0].estimate_site_info_id;
      setActiveTabKey(estimate_site_info_id.toString());
      setActiveEstimateSiteInfoId(estimate_site_info_id);
    }
  }, [EstimateSites, activeTabKey, fetchEstimateCycles, setActiveEstimateSiteInfoId]);

  /*** 일반 유틸리티 함수 ***/
  function downloadEmissionTemplate() {
    const basePath = '/documents/hlmando25/';
    let fileName = '';

    if (templateType === 'legacy') {
      // Excel 2016 등 구버전 호환용
      switch (i18n.language) {
        case 'ko':
          fileName = 'QESG_HL만도 공급망 온실가스 산정 템플릿_2016v4.xlsm';
          break;
        case 'en':
          fileName =
            "QESG_Template for GHG Emission Calculation of HL Mando's Supply Chain_(company name)_2016v4.xlsm";
          break;
        case 'zh':
          fileName = 'QESG_HL Mando供应链温室气体排放核算模板_(企业名称)_2016v4.xlsm';
          break;
        default:
          fileName = 'QESG_HL만도 공급망 온실가스 산정 템플릿_2016v4.xlsm';
          break;
      }
    } else {
      // 온실가스 산정 템플릿 (최신버전)
      switch (i18n.language) {
        case 'ko':
          fileName = 'QESG_HL만도 공급망 온실가스 산정 템플릿_(기업명)ver5.xlsm';
          break;
        case 'en':
          fileName =
            "QESG_Template for GHG Emission Calculation of HL Mando's Supply Chain_(company name)ver5.xlsm";
          break;
        case 'zh':
          fileName = 'QESG_HL Mando供应链温室气体排放核算模板_(企业名称)ver5.xlsm';
          break;
        default:
          fileName = 'QESG_HL만도 공급망 온실가스 산정 템플릿_(기업명)ver5.xlsm';
          break;
      }
    }

    const link = document.createElement('a');
    link.href = basePath + fileName;
    link.download = fileName;
    link.click();
  }

  function downloadManual() {
    const basePath = '/documents/hlmando25/';
    let fileName = '';

    switch (i18n.language) {
      case 'ko':
        fileName = '(QESG) 2025 HL 만도 협력사 온실가스 배출량 산정 교육.pdf';
        break;
      case 'en':
        fileName = '(QESG) 2025 GHG Emissions Calculation for HL Mando’s Global Suppliers.pdf';
        break;
      case 'zh':
        fileName = '(QESG) 2025 HL Mando 合作伙伴温室气体排放量核算培训.pdf';
        break;
      default:
        fileName = '(QESG) 2025 HL 만도 협력사 온실가스 배출량 산정 교육.pdf';
        break;
    }

    const link = document.createElement('a');
    link.href = basePath + fileName;
    link.download = fileName;
    link.click();
  }
  /*** 일반 유틸리티 함수 ***/

  /*** 프로젝트 동의 ***/
  function EstimateAgree() {
    const user = useAuthStore((state) => state.user);
    // const [isPolicyAgree, setPolicyAgree] = useState(false);
    const [agreeDate, setAgreeDate] = useState(null);
    const [isAbleToAgreeSubmit, setAbleToAgreeSubmit] = useState(false);
    const sigCanvas = useRef(null);

    // const onChangeAgree = (e) => {
    //   setPolicyAgree(e.target.value);
    // };

    useEffect(() => {
      showProjectPolicyModal(true);
      setAgreeDate(new Date().toLocaleDateString());
    }, []);

    function handlePolicySubmitAvailable() {
      if (!sigCanvas.current.isEmpty()) {
        setAbleToAgreeSubmit(true);
      } else {
        setAbleToAgreeSubmit(false);
      }
    }

    function clearPolicySignature() {
      sigCanvas.current.clear();
      setAbleToAgreeSubmit(false);
    }

    async function handlePolicySubmit() {
      try {
        // API 호출하여 policy_agree 업데이트
        await API.put(
          `/projects/${EstimateInfo.project.project_id}/estimates/${EstimateInfo.estimate_info_id}`,
          {
            policy_agree: true,
          }
        );

        // 모달 닫기
        showProjectPolicyModal(false);
        // setPolicyAgree(false);
        setAbleToAgreeSubmit(false);
        sigCanvas.current.clear();
      } catch (error) {
        console.error('Error updating policy agree:', error);
        // 오류 발생 시 모달은 열린 상태로 유지
      } finally {
        await fetchEstimateInfo();
      }
    }

    return (
      <>
        <div className="text-end mb-3">
          <LanguageSwitcher />
        </div>
        <div className="bg-light p-4 mb-4">
          {i18n.language === 'ko' && (
            <>
              <Row className="mb-4">
                <Col xs={24}>
                  <div className="text-center">
                    <Typography.Title level={3}>
                      HL만도 글로벌 협력사 온실가스 배출량 정보 등록
                    </Typography.Title>
                    <Typography.Title level={4}>공급망 ESG 협력 이행 서약서</Typography.Title>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <p className="fs-6">
                    서약자는 HL만도의 온실가스 배출량 정보 등록 시스템을 통해 제공하는 모든 정보가
                    사실에 기반하고, 윤리적 기준을 준수함을 다음과 같이 서약합니다.
                  </p>
                </Col>
                <Col xs={24}>
                  <Divider />
                </Col>
                <Col xs={24}>
                  <p className="fs-6">
                    ① 서약자는 온실가스 배출량 정보 등록 시스템에 입력하는 사업장 정보, 온실가스
                    배출량, 총 매출액 등 모든 데이터 및 정보에 대해 사실에 기반하여 성실하고
                    정직하게 작성하겠습니다.
                  </p>
                  <p className="fs-6">
                    ② 서약자는 정보 제출 과정에서 고의적 누락, 허위작성, 증빙자료 조작 등 부정행위를
                    하지 않으며, 법적/윤리적 기준을 위반하지 않도록 최선을 다하겠습니다.
                  </p>
                  <p className="fs-6">
                    ③ 서약자는 제출한 정보에 대해 지속적인 책임을 지며, HL만도의 요청이 있을 경우
                    자료 보완을 성실히 수행하고 기타 필요한 협조를 제공하겠습니다.{' '}
                  </p>
                </Col>
              </Row>
            </>
          )}
          {i18n.language === 'en' && (
            <>
              <Row className="mb-4">
                <Col xs={24}>
                  <div className="text-center">
                    <Typography.Title level={4}>
                      HL Mando Global Supplier GHG Emissions Data Submission
                    </Typography.Title>
                    <Typography.Title level={5}>
                      Supply Chain ESG Cooperation Commitment
                    </Typography.Title>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <p className="fs-6">
                    The signatory hereby pledges that all information provided through HL Mando’s
                    GHG emissions data registration system is based on facts and complies with
                    ethical standards.
                  </p>
                </Col>
                <Col xs={24}>
                  <Divider />
                </Col>
                <Col xs={24}>
                  <p className="fs-6">
                    ① The signatory shall provide accurate and honest information regarding facility
                    details, GHG emissions, total revenue, and all other data and information
                    submitted to the GHG emissions data registration system.
                  </p>
                  <p className="fs-6">
                    ② The signatory shall not engage in misconduct such as intentional omissions,
                    false reporting, or manipulation of supporting documents during the submission
                    process, and shall make every effort to comply with legal and ethical standards.
                  </p>
                  <p className="fs-6">
                    ③ The signatory shall take ongoing responsibility for the submitted information,
                    provide supplementary data upon HL Mando’s request, and cooperate fully with any
                    additional requirements.{' '}
                  </p>
                </Col>
              </Row>
            </>
          )}
          {i18n.language === 'zh' && (
            <>
              <Row className="mb-4">
                <Col xs={24}>
                  <div className="text-center">
                    <Typography.Title level={4}>
                      HL Mando全球合作伙伴温室气体排放信息登记
                    </Typography.Title>
                    <Typography.Title level={5}>供应链ESG合作履约承诺书</Typography.Title>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <p className="fs-6">
                    承诺人郑重承诺，通过HL万都温室气体排放信息登记系统所提供的所有信息均基于事实，且遵守伦理标准。
                  </p>
                </Col>
                <Col xs={24}>
                  <Divider />
                </Col>
                <Col xs={24}>
                  <p className="fs-6">
                    ①
                    签署方应如实、准确地提供有关设施信息、温室气体排放、总收入以及提交至温室气体排放数据登记系统的所有其他数据和信息。
                  </p>
                  <p className="fs-6">
                    ②
                    承诺人在提交信息过程中，不进行故意遗漏、虚假填写、篡改证明材料等不当行为，并尽最大努力不违反法律和伦理标准。
                  </p>
                  <p className="fs-6">
                    ③
                    承诺人对所提交的信息承担持续责任，若HL万都提出要求，将积极配合补充资料并提供其他必要的协助。{' '}
                  </p>
                </Col>
              </Row>
            </>
          )}
          <Row>
            <Col xs={24}>
              <Divider />
            </Col>
            <Col xs={24} className="mb-3" style={{ fontSize: '14px' }}>
              <Row gutter={[12, 12]} className="mb-1">
                <Col xs={10} lg={i18n.language === 'en' ? 12 : 11}>
                  <div className="text-end">
                    {t('제출일')}:{'  '}
                  </div>
                </Col>
                <Col xs={12} lg={12}>
                  <div className="text-start">
                    {i18n.language === 'ko' && dayjs(agreeDate).format('YYYY년 MM월 DD일')}
                    {i18n.language === 'en' && dayjs(agreeDate).format('MM DD, YYYY')}
                    {i18n.language === 'zh' &&
                      dayjs(agreeDate).format('YYYY년 MM月 DD日 (提交日期)')}
                  </div>
                </Col>
              </Row>
              <Row gutter={[12, 12]} className="mb-1">
                <Col xs={10} lg={i18n.language === 'en' ? 12 : 11}>
                  <div className="text-end">
                    {t('기업명')}:{'  '}
                  </div>
                </Col>
                <Col xs={12} lg={12}>
                  <div className="text-start">{user.company_name}</div>
                </Col>
              </Row>
              <Row gutter={[12, 12]} className="mb-1">
                <Col xs={10} lg={i18n.language === 'en' ? 12 : 11}>
                  <div className="text-end">
                    {t('이메일')}:{'  '}
                  </div>
                </Col>
                <Col xs={12} lg={12}>
                  <div className="text-start">{user.email}</div>
                </Col>
              </Row>
              <Row gutter={[12, 12]}>
                <Col xs={10} lg={i18n.language === 'en' ? 12 : 11}>
                  <div className="text-end">
                    {t('서약자')}:{'  '}
                  </div>
                </Col>
                <Col xs={12} lg={12}>
                  <div className="text-start fs-6 mb-1">{user.username}</div>
                </Col>
              </Row>
            </Col>
            <Col xs={24} lg={{ span: 20, offset: 2 }} className="mb-3">
              <div className="d-flex align-items-center mx-auto">
                <div className="me-3" style={{ wordBreak: 'keep-all' }}>
                  {t('서명')}:
                </div>
                <div className="w-100 h-100 text-center">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      className: 'border border-gray-1',
                      style: {
                        backgroundColor: '#fff',
                        height: '100%',
                        width: '100%',
                        minHeight: '240px',
                        maxHeight: '320px',
                      },
                    }}
                    onEnd={handlePolicySubmitAvailable}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div className="text-center">
          <Button
            className="me-2"
            onClick={() => clearPolicySignature()}
            disabled={!isAbleToAgreeSubmit}
          >
            {t('서명 초기화')}
          </Button>
          <Button disabled={!isAbleToAgreeSubmit} onClick={() => handlePolicySubmit()}>
            {t('제출하기')}
          </Button>
        </div>
      </>
    );
  }
  /*** 프로젝트 동의 ***/

  /*** 사업장 추가 및 수정 관련 ***/
  const [siteEditForm] = Form.useForm();
  const openNewSiteModal = () => {
    setSiteModalEdittype(false);
    siteEditForm.setFieldsValue({
      site_name: null,
      site_country: null,
      site_location: null,
      site_clients: [],
    });
    setSiteEditModal(true);
  };

  const openSiteInfoEditModal = (_Site) => {
    setSiteModalEdittype(true);
    siteEditForm.setFieldsValue({
      site_name: _Site.site_name,
      site_country: _Site.site_country,
      site_location: _Site.site_location,
      site_clients: _Site.site_clients,
    });
    setSiteEditModal(true);
  };

  const closeSiteInfoEditModal = () => {
    setSiteModalEdittype(true);
    setSiteEditModal(false);
    siteEditForm.setFieldsValue({});
  };

  const initSiteInfoEditModal = () => {
    siteEditForm.setFieldsValue({
      site_name: null,
      site_country: null,
      site_address: null,
      site_clients: [],
    });
  };

  const saveSiteEditedNewSite = async (siteData, isEdit, siteId, projectId) => {
    try {
      const { site_name, site_country, site_location, site_clients } = siteData;
      let ResSite = null;

      if (!isEdit) {
        const ResNewSite = await API.post('/sites', {
          project_id: projectId,
          company_id: user.company_id,
          site_name: site_name,
          site_location,
          site_country,
          site_clients,
        });
        ResSite = ResNewSite.data;
      } else {
        const ResUpdatedSite = await API.put(`/sites/${siteId}`, {
          company_id: user.company_id,
          site_name: site_name,
          site_location,
          site_country,
          site_clients,
        });
        ResSite = ResUpdatedSite.data;
      }

      return ResSite;
    } catch (error) {
      console.error('Error saving site:', error);
      throw error;
    }
  };

  const saveSiteHandler = async () => {
    try {
      // 폼 검증 실행
      await siteEditForm.validateFields();
      const editedSite = siteEditForm.getFieldsValue();
      const EditingSite = EstimateSites.find(
        ({ estimate_site_info_id }) => ActiveEstimateSiteInfoId === estimate_site_info_id
      );

      await saveSiteEditedNewSite(
        editedSite,
        isSiteEditModal,
        EditingSite?.site_id,
        EstimateInfo?.project?.project_id
      );

      setSiteEditModal(false);
    } catch (error) {
      console.error('Error saving site:', error);
    } finally {
      await fetchEstimateSites(estimate_id);
      initSiteInfoEditModal();
    }
  };
  /*** 사업장 추가 및 수정 관련 ***/

  if (PageLoading) {
    return (
      <BasicLayout body_classname="bg-light">
        <Row gutter={[12, 12]}>
          <Col xs={12}>
            <Skeleton.Input active size="large" />
          </Col>
          <Col xs={12} className="text-end">
            <Skeleton.Button active />
          </Col>
        </Row>
      </BasicLayout>
    );
  }

  if (!PageLoading) {
    return (
      <BasicLayout body_classname="bg-light">
        {/* 페이지 - 헤더 */}
        <Row gutter={[12, 12]} className="mb-4">
          <Col xs={22}>
            <Typography.Title level={1}>
              {EstimateInfo?.project?.[`project_name_${i18n.language}`]}
            </Typography.Title>
          </Col>
          <Col xs={2} className="text-end">
            <Button loading={PageLoading} onClick={() => fetchEstimateInfo()}>
              새로고침
            </Button>
          </Col>
        </Row>
        {/* 페이지 - 본문 */}
        {/* 프로젝트 동의 모달 */}
        <Modal
          title={t('프로젝트 동의')}
          open={isShowProjectPolicyModal}
          onOk={null}
          onCancel={null}
          width={'60%'}
          footer={null}
          style={{ maxWidth: '860px' }}
          className="my-4"
          centered
          closable={false}
          maskClosable={false}
        >
          <EstimateAgree />
        </Modal>
        {/* 사업장 관리 모달 */}
        <SiteEditModal
          isVisible={isShowSiteEditModal}
          isEdit={isSiteEditModal}
          form={siteEditForm}
          onSave={saveSiteHandler}
          onCancel={closeSiteInfoEditModal}
          onClose={() => setSiteEditModal(false)}
          onReset={initSiteInfoEditModal}
        />
        {/* 프로젝트 정보 */}
        <Row gutter={[24, 24]} className="mb-5">
          <Col xs={24} xl={12}>
            <Card className="shadow-sm">
              <Typography.Title level={3} className="text-dark mb-3">
                {t('프로젝트 정보')}
              </Typography.Title>

              <Row>
                <Col xs={24} lg={24}>
                  <Descriptions
                    column={{ xs: 6, lg: 3, xl: 2 }}
                    size="small"
                    className="border"
                    labelStyle={STYLE_CONSTANTS.DESC_STYLE_HEADER}
                    contentStyle={STYLE_CONSTANTS.DESC_STYLE_BODY}
                    bordered
                  >
                    <Descriptions.Item label={t('프로젝트명')}>
                      {EstimateInfo?.project?.[`project_name_${i18n.language}`]}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('기간')}>
                      {formatDateRange(
                        EstimateInfo?.project['start_date'],
                        EstimateInfo?.project['end_date'],
                        userTimezone
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('남은 기간')}>
                      {RemainingDays(EstimateInfo?.project['end_date'])}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('프로젝트 상태')}>
                      <Tag
                        color={ProjectStatusCodeColor[EstimateInfo?.project['status_code']]}
                        style={{ fontSize: '12px', lineHeight: 2.1 }}
                      >
                        {t(ProjectStatusCode[EstimateInfo?.project['status_code']])}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('안내사항 및 메뉴얼')}>
                      <Button className="me-2" onClick={() => showProjectAnnouncementModal(true)}>
                        {t('안내사항 보기')}
                      </Button>
                      <Button icon={<DownloadOutlined />} onClick={downloadManual}>
                        {t('메뉴얼 다운로드')}
                      </Button>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* 프로젝트 안내 모달 */}
          <ProjectAnnouncementModal
            isVisible={isShowProjectAnnouncementModal}
            onClose={() => showProjectAnnouncementModal(false)}
          />

          <Col xs={24} xl={12}>
            <Card className="shadow-sm">
              <div className="d-flex align-items-bottom justify-content-between">
                <Typography.Title level={3} className="text-dark mb-3">
                  {t('기업 정보')}
                </Typography.Title>
              </div>

              <Row>
                <Col xs={24}>
                  <Descriptions
                    column={1}
                    size="small"
                    className="border"
                    // layout="vertical"
                    labelStyle={STYLE_CONSTANTS.DESC_STYLE_HEADER}
                    contentStyle={STYLE_CONSTANTS.DESC_STYLE_BODY}
                    bordered
                  >
                    <Descriptions.Item label={t('기업명')}>
                      {EstimateInfo?.company.company_name}
                    </Descriptions.Item>
                    <Descriptions.Item label={`${t('기업')} ${t('진행 상태')}`}>
                      <Tag
                        color={ProjectStatusCodeColor[EstimateInfo?.estimate_info_status]}
                        style={{ fontSize: '12px', lineHeight: 2.1 }}
                      >
                        {t(ProjectStatusCode[EstimateInfo?.estimate_info_status])}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('참여신청일')}>
                      {formatDateWithTimezone(EstimateInfo?.vendor_code.used_at)}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        {/* 사업장 관리 모달 */}
        <SiteEditModal
          isVisible={isShowSiteEditModal}
          isEdit={isSiteEditModal}
          form={siteEditForm}
          onSave={saveSiteHandler}
          onCancel={closeSiteInfoEditModal}
          onClose={() => setSiteEditModal(false)}
          onReset={initSiteInfoEditModal}
        />
        {/* 품번조회 관련 */}
        <Modal
          title={`${t('품번조회')}`}
          open={isShowScope3TemplateModal}
          onCancel={() => setShowScope3TemplateModal(false)}
          width="60%"
          centered
          footer={[
            <div
              key="footer"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <LanguageSwitcher />
              <Button onClick={() => setShowScope3TemplateModal(false)}>{t('닫기')}</Button>
            </div>,
          ]}
        >
          <MaterialSearchTable />
        </Modal>
        <Row className="mb-5">
          <Col xs={24}>
            <Card
              bodyStyle={{
                paddingBottom: 0,
                ...(EstimateSites.length > 0 && { paddingLeft: 0, paddingRight: 0 }),
              }}
            >
              <div
                className={clsx(
                  'd-flex justify-content-between align-items-bottom',
                  EstimateSites.length > 0 && 'px-4',
                  'mb-2'
                )}
              >
                <Typography.Title level={3} className="text-dark mb-0">
                  {t('사업장')} {t('목록')}
                </Typography.Title>
                <Button onClick={() => openNewSiteModal()}>{t('사업장 추가')}</Button>
                {/* <Button>{t('사업장 추가')}</Button> */}
              </div>

              {!EstimateSites.length && (
                <Row>
                  <Col xs={24}>
                    <div className="text-center bg-gray-1 py-5 mb-4">
                      <Typography.Title level={3} className="fw-medium text-gray-4 mb-0">
                        신규 사업장을 추가해주세요.
                      </Typography.Title>
                    </div>
                  </Col>
                </Row>
              )}

              {EstimateSites.length > 0 && (
                <Card
                  className="border-0"
                  bodyStyle={{ padding: 0 }}
                  tabList={EstimateSites.map((site) => ({
                    tab: site.site_name,
                    key: site.estimate_site_info_id.toString(),
                  }))}
                  onTabChange={(key) => {
                    setActiveTabKey(key);
                    setActiveEstimateSiteInfoId(parseInt(key));
                    // memo: 탭 변경 시 key가 문자열인 탓에 521번째줄 saveSiteHandler 에서 EditingSite값이 undefined가 된 이슈가 있었음
                    // key를 문자열로 해야하는 이유가 있다면 여기를 parseInt로 해야하고, 아니라면 위에서 그대로 number로 받아도 될 것
                  }}
                  activeTabKey={activeTabKey}
                >
                  {(() => {
                    const activeSite = EstimateSites.find(
                      (site) => site.estimate_site_info_id.toString() === activeTabKey
                    );
                    return activeSite ? (
                      <div className="p-3">
                        <Row className="mb-5">
                          <Col xs={24} className="mb-3">
                            <div className="d-flex justify-content-between">
                              <Typography.Title level={4} className="text-dark mb-0">
                                {t('사업장 정보')}
                              </Typography.Title>
                              <Button
                                icon={<FormOutlined />}
                                onClick={() => openSiteInfoEditModal(activeSite)}
                              >
                                {t('사업장 정보 수정')}
                              </Button>
                            </div>
                          </Col>
                          <Col xs={24}>
                            <SiteCard
                              site={{
                                ...activeSite,
                                company_name: EstimateInfo?.company.company_name,
                              }}
                            />
                          </Col>
                        </Row>
                        <Row className="mb-2">
                          <Col xs={12}>
                            <Typography.Title level={4} className="text-dark mb-0">
                              {t('산정 증빙자료 제출')}
                            </Typography.Title>
                          </Col>
                          <Col xs={12}>
                            <div className="d-flex align-items-center justify-content-end">
                              <div className="fw-medium me-2" style={{ fontSize: '15px' }}>
                                {t('산정 템플릿')}:
                              </div>
                              <Select
                                value={templateType}
                                onChange={setTemplateType}
                                // style={{ width: 200, marginRight: 8 }}
                                options={[
                                  {
                                    value: 'latest',
                                    label: `${t('온실가스 산정 템플릿')} v5`,
                                  },
                                  {
                                    value: 'legacy',
                                    label: t('Excel 2016 등 구버전 호환용'),
                                  },
                                ]}
                              />
                              <Button
                                icon={<DownloadOutlined />}
                                onClick={downloadEmissionTemplate}
                              >
                                {t('템플릿 다운로드')}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col xs={24} xl={12}>
                            <div className="d-flex">
                              <div className="fw-medium me-2" style={{ fontSize: '15px' }}>
                                {t('회차별 상태')}:
                              </div>
                              {Object.entries(StatusColor).map(([state]) => (
                                <Tag color={StatusColor[state]} key={state}>
                                  {t(CycleStatus[state])}
                                </Tag>
                              ))}
                            </div>
                          </Col>
                          <Col xs={24} xl={12} className="text-end">
                            <Button
                              className="me-2"
                              onClick={() => setShowScope3TemplateModal(true)}
                            >
                              {t('품번조회')}
                            </Button>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={24}>
                            <EstimateCycleTable />
                          </Col>
                        </Row>
                      </div>
                    ) : null;
                  })()}
                </Card>
              )}
            </Card>
          </Col>
        </Row>
      </BasicLayout>
    );
  }
}

export default UserSiteDetailedv2;
